import { useEffect, useRef, useState } from "react";
import {
  Send, ArrowLeft, Check, CheckCheck, X, Edit2, Trash2, Image as ImageIcon,
  Search, Smile, MoreVertical,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useMatchChat } from "@/lib/useMatchChat";
import EmojiPanel from "@/components/EmojiPanel";
import { toast } from "sonner";

const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// P3 FIX: Use environment variable
const CDN_HOST = process.env.REACT_APP_CDN_HOST || "img.havenpositive.online";

function bubbleImageUrl(url, width = 720, quality = 88) {
  if (!url) return url;
  if (!url.includes(CDN_HOST)) return url;
  return url
    .replace(/([?&])w=\d+/, `$1w=${width}`)
    .replace(/([?&])q=\d+/, `$1q=${quality}`);
}

export default function MatchChat({ match, onBack }) {
  const { user } = useAuth();
  const [input, setInput] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewerImage, setViewerImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);

  const {
    messages, loading, partnerTyping, partnerLastActive, online,
    sendMessage, sendImage, editMessage, deleteMessage, reactToMessage, notifyTyping,
  } = useMatchChat(match, user?.user_id);

  useEffect(() => {
    if (!showSearch) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, partnerTyping, showSearch]);

  const filtered = searchQuery
    ? messages.filter(
        m => !m.deleted && (m.content || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : messages;

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    const replyId = replyingTo?.message_id;
    const reply = replyingTo;
    setReplyingTo(null);
    try {
      await sendMessage(text, { replyToId: replyId, replyTo: reply });
    } catch {
      toast.error("Failed to send");
      setInput(text);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else {
      notifyTyping();
    }
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    if (!el) {
      setInput(prev => prev + emoji);
      return;
    }
    const start = el.selectionStart ?? input.length;
    const end = el.selectionEnd ?? input.length;
    const next = input.slice(0, start) + emoji + input.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const backspaceInput = () => {
    const el = inputRef.current;
    if (!el || input.length === 0) return;
    const start = el.selectionStart ?? input.length;
    const end = el.selectionEnd ?? input.length;
    if (start !== end) {
      const next = input.slice(0, start) + input.slice(end);
      setInput(next);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start, start);
      });
      return;
    }
    if (start === 0) return;
    const chars = Array.from(input.slice(0, start));
    chars.pop();
    const before = chars.join("");
    const next = before + input.slice(start);
    setInput(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(before.length, before.length);
    });
  };

  const pickImage = () => fileInputRef.current?.click();

  const onImagePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    const replyId = replyingTo?.message_id;
    const reply = replyingTo;
    setReplyingTo(null);
    try {
      await sendImage(file, { replyToId: replyId, replyTo: reply });
    } catch (err) {
      toast.error(err.message || "Failed to send image");
    } finally {
      setUploading(false);
    }
  };

  const saveEdit = async () => {
    const text = editValue.trim();
    if (!text) return;
    try {
      await editMessage(editingId, text);
      setEditingId(null);
      setEditValue("");
    } catch {
      toast.error("Failed to edit");
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await deleteMessage(id); } catch { toast.error("Failed to delete"); }
  };

  const presenceText = () => {
    if (partnerTyping) return "typing…";
    if (!partnerLastActive) return online ? "Online" : "Offline";
    const diff = Date.now() - new Date(partnerLastActive).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Online";
    if (mins < 60) return `Last seen ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Last seen ${hrs}h ago`;
    return `Last seen ${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-[#FAFAF7]">
      <div className="bg-white border-b">
        <div className="px-3 py-3 flex items-center gap-2">
          <button onClick={onBack} className="p-1.5 hover:bg-[#F5F3EE] rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
            {match.profile_image
              ? <img src={match.profile_image} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full grid place-items-center text-[#6B6B70]">👤</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{match.display_name}</div>
            <div className={`text-xs truncate ${partnerTyping ? "text-[#7C3AED]" : "text-[#6B6B70]"}`}>
              {presenceText()}
            </div>
          </div>
          <button
            onClick={() => { setShowSearch(s => !s); setSearchQuery(""); }}
            className={`p-2 rounded-lg hover:bg-[#F5F3EE] ${showSearch ? "bg-[#F5F3EE]" : ""}`}
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
        {showSearch && (
          <div className="px-3 pb-3">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search this conversation…"
              autoFocus
              className="w-full px-4 py-2 bg-[#F5F3EE] rounded-full text-sm outline-none"
            />
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
        {loading && (
          <div className="text-center text-sm text-[#6B6B70] py-8">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-sm text-[#6B6B70] mt-10">
            {searchQuery ? `No matches for "${searchQuery}"` : `💜 Say hello to ${match.display_name}`}
          </div>
        )}

        {filtered.map((m, idx) => {
          const isMe = m.sender_id === user.user_id;
          const status = m._status || (m.read ? "read" : "sent");
          const deleted = m.deleted;
          const showTime = !filtered[idx + 1] || filtered[idx + 1].sender_id !== m.sender_id;
          const showAvatar = !isMe && (!filtered[idx - 1] || filtered[idx - 1].sender_id !== m.sender_id);

          return (
            <div
              key={m.message_id}
              className={`group flex ${isMe ? "justify-end" : "justify-start"} items-end gap-1.5`}
            >
              {!isMe && (
                <div className="w-6 h-6 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
                  {showAvatar && match.profile_image ? (
                    <img src={match.profile_image} alt="" className="w-full h-full object-cover" />
                  ) : showAvatar ? (
                    <div className="w-full h-full grid place-items-center text-[11px] text-[#6B6B70]">
                      {(match.display_name || "?")[0]?.toUpperCase()}
                    </div>
                  ) : null}
                </div>
              )}

              <div className="max-w-[75%]">
                {m.reply_to && !deleted && (
                  <div className={`text-[11px] px-2 py-1 rounded-t-lg mb-0.5 ${
                    isMe ? "bg-purple-100 text-purple-800 ml-auto" : "bg-gray-100 text-gray-600"
                  } max-w-full`}>
                    <div className="font-semibold truncate">
                      {m.reply_to.sender_id === user.user_id ? "You" : match.display_name}
                    </div>
                    <div className="truncate">{m.reply_to.content}</div>
                  </div>
                )}

                <div className="relative">
                  {!isMe && (
                    <div
                      className="absolute top-0 -left-[6px] w-2 h-2 bg-white"
                      style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
                    />
                  )}
                  {isMe && (
                    <div
                      className="absolute bottom-0 -right-[6px] w-2 h-2 bg-[#7C3AED]"
                      style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
                    />
                  )}

                  <div className={`relative rounded-2xl text-sm ${
                    m.message_type === "image" ? "p-1" : "px-3 py-2"
                  } ${
                    isMe
                      ? "bg-[#7C3AED] text-white rounded-br-md"
                      : "bg-white border border-[#E7E5E0] rounded-bl-md"
                  } ${status === "failed" ? "ring-2 ring-red-400" : ""}`}>

                    {editingId === m.message_id ? (
                      <div className="flex gap-1 items-center min-w-[180px]">
                        <input
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          className="bg-transparent outline-none border-b border-white/40 text-white w-full text-sm"
                          autoFocus
                        />
                        <button onClick={saveEdit} className="text-white">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-white/70">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : deleted ? (
                      <em className={isMe ? "text-white/70" : "text-gray-500"}>
                        This message was deleted
                      </em>
                    ) : m.message_type === "image" && m.media_url ? (
                      <div className="relative">
                        <img
                          src={bubbleImageUrl(m.media_url, 720, 88)}
                          alt=""
                          onClick={() => setViewerImage(m.media_url)}
                          className="rounded-xl max-w-[240px] max-h-[320px] object-cover cursor-pointer"
                        />
                        <div className="absolute bottom-1 right-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/55 text-white text-[10px]">
                          {m.edited && !deleted && <span className="opacity-90">edited</span>}
                          {showTime && (
                            <span>
                              {new Date(m.created_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          )}
                          {isMe && status === "sending" && <span>·</span>}
                          {isMe && status === "sent" && <Check className="w-3 h-3" />}
                          {isMe && status === "read" && <CheckCheck className="w-3 h-3 text-blue-300" />}
                          {isMe && status === "failed" && (
                            <span className="text-red-300 font-medium">failed</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      m.content
                    )}

                    {m.message_type !== "image" && (
                      <div className={`flex items-center justify-end gap-1 mt-0.5 ${
                        isMe ? "text-purple-200" : "text-gray-400"
                      } text-[10px]`}>
                        {m.edited && !deleted && <span>edited</span>}
                        {showTime && (
                          <span>
                            {new Date(m.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        )}
                        {isMe && status === "sending" && <span>·</span>}
                        {isMe && status === "sent" && <Check className="w-3 h-3" />}
                        {isMe && status === "read" && (
                          <CheckCheck className="w-3 h-3 text-blue-300" />
                        )}
                        {isMe && status === "failed" && (
                          <span className="text-red-300 font-medium">failed</span>
                        )}
                      </div>
                    )}
                  </div>

                  {!deleted && editingId !== m.message_id && (
                    <div className={`absolute -top-8 z-10 hidden group-hover:flex items-center gap-0.5 bg-white border border-[#E7E5E0] rounded-lg shadow-lg p-1 ${
                      isMe ? "right-0" : "left-0"
                    }`}>
                      <button
                        onClick={() => setReplyingTo(m)}
                        title="Reply"
                        className="p-1.5 hover:bg-gray-100 rounded text-sm"
                      >
                        ↩
                      </button>
                      {isMe && m.message_type === "text" && (
                        <button
                          onClick={() => { setEditingId(m.message_id); setEditValue(m.content); }}
                          title="Edit"
                          className="p-1.5 hover:bg-gray-100 rounded"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {isMe && (
                        <button
                          onClick={() => doDelete(m.message_id)}
                          title="Delete"
                          className="p-1.5 hover:bg-gray-100 rounded"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      )}
                      <div className="flex gap-0.5 border-l border-[#E7E5E0] pl-1 ml-0.5">
                        {REACTIONS.map(e => (
                          <button
                            key={e}
                            onClick={() => reactToMessage(m.message_id, e)}
                            className="px-1 hover:bg-gray-100 rounded text-lg leading-none"
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {m.reactions && Object.keys(m.reactions).length > 0 && (
                  <div className={`flex gap-1 mt-1 flex-wrap ${isMe ? "justify-end" : "justify-start"}`}>
                    {Object.entries(
                      Object.values(m.reactions).reduce((acc, e) => {
                        acc[e] = (acc[e] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([emoji, count]) => (
                      <span
                        key={emoji}
                        className="px-2 py-0.5 bg-white border border-[#E7E5E0] rounded-full text-[11px] shadow-sm"
                      >
                        {emoji}{count > 1 ? ` ${count}` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {partnerTyping && !searchQuery && (
          <div className="flex gap-1 text-[#7C3AED] text-sm pl-8 items-center">
            <span className="w-1.5 h-1.5 bg-[#7C3AED] rounded-full animate-bounce" />
            <span className="w-1.5 h-1.5 bg-[#7C3AED] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 bg-[#7C3AED] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            <span className="text-xs ml-1">typing…</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {replyingTo && (
        <div className="px-4 py-2 bg-[#F5F3EE] border-t border-[#E7E5E0] flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-[#7C3AED]">
              Replying to {replyingTo.sender_id === user.user_id ? "You" : match.display_name}
            </div>
            <div className="text-xs text-[#6B6B70] truncate">
              {replyingTo.message_type === "image" ? "📷 Photo" : replyingTo.content}
            </div>
          </div>
          <button onClick={() => setReplyingTo(null)} className="p-1">
            <X className="w-4 h-4 text-[#6B6B70]" />
          </button>
        </div>
      )}

      {showEmoji && (
        <EmojiPanel
          onEmoji={insertEmoji}
          onBackspace={backspaceInput}
        />
      )}

      <div className="px-3 py-3 border-t border-[#E7E5E0] bg-white flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onImagePicked}
          className="hidden"
        />
        <button
          onClick={pickImage}
          disabled={uploading}
          className="w-10 h-10 rounded-full flex items-center justify-center text-[#7C3AED] hover:bg-[#F5F3EE] disabled:opacity-50"
          title="Send image"
        >
          {uploading
            ? <div className="w-4 h-4 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
            : <ImageIcon className="w-5 h-5" />}
        </button>
        <button
          onClick={() => setShowEmoji(s => !s)}
          className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#F5F3EE] ${
            showEmoji ? "text-[#7C3AED] bg-[#F3E8FF]" : "text-[#F59E0B]"
          }`}
          title="Emoji"
        >
          <Smile className="w-5 h-5" />
        </button>
        <input
          ref={inputRef}
          value={input}
          onChange={e => { setInput(e.target.value); notifyTyping(); }}
          onKeyDown={handleKey}
          onFocus={() => setShowEmoji(false)}
          placeholder="Type a message…"
          className="flex-1 px-4 py-2.5 bg-[#F5F3EE] rounded-full text-sm outline-none"
          maxLength={5000}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim()}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            input.trim()
              ? "bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
              : "bg-gray-300 text-gray-500"
          }`}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      {viewerImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setViewerImage(null)}
        >
          <button
            onClick={() => setViewerImage(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={viewerImage}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}